import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Switch,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  MonetizationOn as CoinIcon,
  CurrencyRupee as RupeeIcon,
  Redeem as RedeemIcon,
  Group as UsersIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon,
  Edit as EditIcon,
  MoreHoriz as MoreIcon,
  Close as CloseIcon,
  ContentCopy as DuplicateIcon,
  Block as DeactivateIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';

// ─── Theme color (matching SubscriptionManagement) ───
const ACCENT = '#6D53F4';
const ACCENT_HOVER = '#5A3FE0';
const ACCENT_LIGHT = '#F0EDFE';

// ─── Sub-tab definitions ───
const SUB_TABS = ['Coin Packages', 'Earning Tasks', 'Rewards', 'Wallet Analytics', 'Settings'];

// ─── Country / Currency data ───
const COUNTRIES = ['India', 'United States', 'United Kingdom', 'Canada', 'Australia'];
const CURRENCY_MAP = {
  India: 'INR (₹)',
  'United States': 'USD ($)',
  'United Kingdom': 'GBP (£)',
  Canada: 'CAD ($)',
  Australia: 'AUD ($)',
};

// ─── Stats data ───
const STATS = [
  {
    label: 'Total Coins\nCirculating',
    value: '1,200,000',
    change: '+8.2%',
    positive: true,
    icon: <CoinIcon />,
    iconBg: '#EDE9FE',
    iconColor: ACCENT,
  },
  {
    label: 'Total Revenue',
    value: '₹12,50,000',
    change: '+14.5%',
    positive: true,
    icon: <RupeeIcon />,
    iconBg: '#EDE9FE',
    iconColor: ACCENT,
  },
  {
    label: 'Coins Redeemed',
    value: '450,000',
    change: '+3.1%',
    positive: true,
    icon: <RedeemIcon />,
    iconBg: '#F0FDF4',
    iconColor: '#16A34A',
  },
  {
    label: 'Active Wallet Users',
    value: '24,500',
    change: '-1.2%',
    positive: false,
    icon: <UsersIcon />,
    iconBg: '#EDE9FE',
    iconColor: ACCENT,
  },
];

// ─── Mock Earning Tasks ───
const mockEarningTasks = [
  {
    id: 1,
    name: 'Connect With 5 New People',
    rewardCoins: 20,
    requirement: 'Make 5 connections',
    status: 'Active',
  },
  {
    id: 2,
    name: 'Send 10 Messages',
    rewardCoins: 15,
    requirement: 'Start 10 conversations',
    status: 'Active',
  },
  {
    id: 3,
    name: 'Profile Verification',
    rewardCoins: 25,
    requirement: 'Verify profile',
    status: 'Active',
  },
  {
    id: 4,
    name: 'Daily Login Bonus',
    rewardCoins: 5,
    requirement: 'Daily login reward',
    status: 'Active',
  },
];

// ─── Mock Coin Packages ───
const mockCoinPackages = [
  {
    id: 1,
    name: 'Starter Pack',
    coins: '1,200',
    price: '₹199',
    bonusCoins: '—',
    popularity: 'Popular',
    status: 'Active',
  },
  {
    id: 2,
    name: 'Value Pack',
    coins: '2,400',
    price: '₹399',
    bonusCoins: '200',
    popularity: 'Normal',
    status: 'Active',
  },
  {
    id: 3,
    name: 'Best Value Pack',
    coins: '5,500',
    price: '₹799',
    bonusCoins: '500',
    popularity: 'Best Value',
    status: 'Active',
  },
  {
    id: 4,
    name: 'Premium Pack',
    coins: '12,000',
    price: '₹1,499',
    bonusCoins: '1,500',
    popularity: 'Premium',
    status: 'Active',
  },
];

// ─── Mock Consumable Purchases ───
const mockConsumables = [
  {
    id: 1,
    name: 'Super Like',
    coinsRequired: '15',
    description: 'Boost profile reach',
    status: 'Disabled',
  },
  {
    id: 2,
    name: 'Profile Boost',
    coinsRequired: '50',
    description: 'Increase visibility',
    status: 'Active',
  },
  {
    id: 3,
    name: 'Priority Message',
    coinsRequired: '25',
    description: 'Highlight message',
    status: 'Active',
  },
];

// ─── Mock Rewards ───
const mockRewards = [
  {
    id: 1,
    name: 'Profile Verification',
    type: 'Free Connect',
    value: '5 User Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 2,
    name: 'Profile Verification',
    type: 'LBK coins',
    value: '0 Coin Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 3,
    name: 'Profile Verification',
    type: 'Free Post',
    value: '5 Post Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 4,
    name: 'Profile Verification',
    type: 'Top Profile',
    value: '5 Days Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 5,
    name: 'Profile Verification',
    type: 'Top Post Listing',
    value: '5 Days Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 6,
    name: 'Profile Verification',
    type: 'Who Likes You',
    value: '5 User Count',
    usersEarned: '4,250',
    status: 'Active',
  },
  {
    id: 7,
    name: 'Onboarding Process',
    type: 'Super Like',
    value: '5 User Count',
    usersEarned: '2,100',
    status: 'Active',
  },
];

// ─── Shared table header style ───
const thStyle = {
  fontWeight: 600,
  color: '#6B7280',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  borderBottom: '1px solid #E5E7EB',
  bgcolor: '#FAFAFA',
  py: 1.5,
};

const LBKWalletManagement = () => {
  const [activeTab, setActiveTab] = useState('Coin Packages');
  const [country, setCountry] = useState('India');
  const [currency, setCurrency] = useState('INR (₹)');

  // ─── Coin Packages & Consumables State ───
  const [coinPackages, setCoinPackages] = useState(mockCoinPackages);
  const [consumables, setConsumables] = useState(mockConsumables);

  // Modals state for Coin Packages
  const [openAddPackageModal, setOpenAddPackageModal] = useState(false);
  const [openEditPackageModal, setOpenEditPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  
  // Package Form State
  const [pkgName, setPkgName] = useState('');
  const [pkgCoins, setPkgCoins] = useState('');
  const [pkgPrice, setPkgPrice] = useState('');
  const [pkgBonus, setPkgBonus] = useState('');
  const [pkgPopularity, setPkgPopularity] = useState('');
  const [pkgStatus, setPkgStatus] = useState('Active');

  const handleOpenEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPkgName(pkg.name);
    setPkgCoins(pkg.coins.replace(/,/g, ''));
    setPkgPrice(pkg.price);
    setPkgBonus(pkg.bonusCoins === '—' ? '0' : pkg.bonusCoins.replace(/,/g, ''));
    setPkgPopularity(pkg.popularity);
    setPkgStatus(pkg.status);
    setOpenEditPackageModal(true);
  };

  const handleClosePackageModal = () => {
    setOpenAddPackageModal(false);
    setOpenEditPackageModal(false);
    setEditingPackage(null);
    setPkgName('');
    setPkgCoins('');
    setPkgPrice('');
    setPkgBonus('');
    setPkgPopularity('');
    setPkgStatus('Active');
  };

  const handleSavePackage = () => {
    if (!pkgName || !pkgCoins || !pkgPrice) {
      alert('Please fill out required fields');
      return;
    }
    const formattedCoins = Number(pkgCoins).toLocaleString();
    const formattedBonus = pkgBonus && pkgBonus !== '0' ? Number(pkgBonus).toLocaleString() : '—';
    
    if (editingPackage) {
      setCoinPackages(coinPackages.map(p => p.id === editingPackage.id ? {
        ...p, name: pkgName, coins: formattedCoins, price: pkgPrice, bonusCoins: formattedBonus, popularity: pkgPopularity, status: pkgStatus
      } : p));
    } else {
      setCoinPackages([...coinPackages, {
        id: coinPackages.length + 1,
        name: pkgName,
        coins: formattedCoins,
        price: pkgPrice,
        bonusCoins: formattedBonus,
        popularity: pkgPopularity || 'Normal',
        status: pkgStatus
      }]);
    }
    handleClosePackageModal();
  };

  // Modals state for Consumables
  const [openAddConsumableModal, setOpenAddConsumableModal] = useState(false);
  const [openEditConsumableModal, setOpenEditConsumableModal] = useState(false);
  const [editingConsumable, setEditingConsumable] = useState(null);

  // Consumable Form State
  const [consName, setConsName] = useState('');
  const [consCoins, setConsCoins] = useState('');
  const [consDesc, setConsDesc] = useState('');
  const [consStatus, setConsStatus] = useState('Active');

  const handleOpenEditConsumable = (cons) => {
    setEditingConsumable(cons);
    setConsName(cons.name);
    setConsCoins(cons.coinsRequired);
    setConsDesc(cons.description);
    setConsStatus(cons.status);
    setOpenEditConsumableModal(true);
  };

  const handleCloseConsumableModal = () => {
    setOpenAddConsumableModal(false);
    setOpenEditConsumableModal(false);
    setEditingConsumable(null);
    setConsName('');
    setConsCoins('');
    setConsDesc('');
    setConsStatus('Active');
  };

  const handleSaveConsumable = () => {
    if (!consName || !consCoins || !consDesc) {
      alert('Please fill out required fields');
      return;
    }
    if (editingConsumable) {
      setConsumables(consumables.map(c => c.id === editingConsumable.id ? {
        ...c, name: consName, coinsRequired: consCoins, description: consDesc, status: consStatus
      } : c));
    } else {
      setConsumables([...consumables, {
        id: consumables.length + 1,
        name: consName,
        coinsRequired: consCoins,
        description: consDesc,
        status: consStatus
      }]);
    }
    handleCloseConsumableModal();
  };

  const handleToggleConsumableStatus = (id) => {
    setConsumables(consumables.map(c => c.id === id ? { ...c, status: c.status === 'Active' ? 'Disabled' : 'Active' } : c));
  };

  const handleDeleteConsumable = (id) => {
    setConsumables(consumables.filter(c => c.id !== id));
  };

  // ─── Rewards state ───
  const [rewards, setRewards] = useState(mockRewards);
  const [openAddRewardModal, setOpenAddRewardModal] = useState(false);
  const [newRewardName, setNewRewardName] = useState('');
  const [newRewardType, setNewRewardType] = useState('');
  const [newRewardValue, setNewRewardValue] = useState('');
  const [newRewardStatus, setNewRewardStatus] = useState('Active');

  const handleAddRewardSubmit = () => {
    if (!newRewardName.trim() || !newRewardValue.trim()) {
      alert('Please fill out all required fields.');
      return;
    }
    const newReward = {
      id: rewards.length + 1,
      name: newRewardName,
      type: newRewardType || 'Custom',
      value: newRewardValue,
      usersEarned: '0',
      status: newRewardStatus,
    };
    setRewards([...rewards, newReward]);
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setNewRewardName('');
    setNewRewardType('');
    setNewRewardValue('');
    setNewRewardStatus('Active');
    setOpenAddRewardModal(false);
  };

  // ─── Edit Reward state ───
  const [openEditRewardModal, setOpenEditRewardModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [editRewardName, setEditRewardName] = useState('');
  const [editRewardType, setEditRewardType] = useState('');
  const [editRewardValue, setEditRewardValue] = useState('');
  const [editRewardStatus, setEditRewardStatus] = useState('Active');

  const handleOpenEditModal = (reward) => {
    setEditingReward(reward);
    setEditRewardName(reward.name);
    setEditRewardType(reward.type);
    setEditRewardValue(reward.value);
    setEditRewardStatus(reward.status);
    setOpenEditRewardModal(true);
  };

  const handleEditRewardSubmit = () => {
    if (!editRewardName.trim() || !editRewardValue.trim()) {
      alert('Please fill out all required fields.');
      return;
    }
    setRewards(rewards.map((r) =>
      r.id === editingReward.id
        ? { ...r, name: editRewardName, type: editRewardType || 'Custom', value: editRewardValue, status: editRewardStatus }
        : r
    ));
    handleCloseEditModal();
  };

  const handleCloseEditModal = () => {
    setEditingReward(null);
    setEditRewardName('');
    setEditRewardType('');
    setEditRewardValue('');
    setEditRewardStatus('Active');
    setOpenEditRewardModal(false);
  };

  // ─── Reward context menu state ───
  const [rewardMenuAnchor, setRewardMenuAnchor] = useState(null);
  const [menuReward, setMenuReward] = useState(null);

  const handleOpenRewardMenu = (event, reward) => {
    setRewardMenuAnchor(event.currentTarget);
    setMenuReward(reward);
  };

  const handleCloseRewardMenu = () => {
    setRewardMenuAnchor(null);
    setMenuReward(null);
  };

  const handleDuplicateReward = () => {
    if (menuReward) {
      const duplicate = {
        ...menuReward,
        id: Math.max(...rewards.map((r) => r.id)) + 1,
        name: `${menuReward.name} (Copy)`,
      };
      setRewards([...rewards, duplicate]);
    }
    handleCloseRewardMenu();
  };

  const handleDeactivateReward = () => {
    if (menuReward) {
      setRewards(rewards.map((r) =>
        r.id === menuReward.id ? { ...r, status: r.status === 'Active' ? 'Inactive' : 'Active' } : r
      ));
    }
    handleCloseRewardMenu();
  };

  const handleDeleteReward = () => {
    if (menuReward) {
      setRewards(rewards.filter((r) => r.id !== menuReward.id));
    }
    handleCloseRewardMenu();
  };

  // ─── Settings state ───
  const [defaultWelcomeCoins, setDefaultWelcomeCoins] = useState('50');
  const [dailyLoginReward, setDailyLoginReward] = useState('5');
  const [maxCoinBalance, setMaxCoinBalance] = useState('100000');
  const [coinExpiryDuration, setCoinExpiryDuration] = useState('365');
  const [enableCoinPurchases, setEnableCoinPurchases] = useState(false);
  const [enableRewardTasks, setEnableRewardTasks] = useState(true);
  const [enableConsumablePurchases, setEnableConsumablePurchases] = useState(true);

  // Handle country change → auto-fill currency
  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setCountry(selectedCountry);
    setCurrency(CURRENCY_MAP[selectedCountry] || 'INR (₹)');
  };

  // ─── Render Settings Tab ───
  const renderSettingsTab = () => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Section Header */}
      <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #F3F4F6' }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
        >
          Wallet Settings
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
          Configure global LBK Wallet behavior.
        </Typography>
      </Box>

      {/* Coin Defaults */}
      <Box sx={{ px: 3, py: 3 }}>
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.08em',
            color: '#6B7280',
            mb: 2.5,
            display: 'block',
          }}
        >
          COIN DEFAULTS
        </Typography>

        {/* Row 1: Default Welcome Coins & Daily Login Reward */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', mb: 0.8, fontSize: '0.875rem' }}
            >
              Default Welcome Coins
            </Typography>
            <TextField
              fullWidth
              value={defaultWelcomeCoins}
              onChange={(e) => setDefaultWelcomeCoins(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
              Coins given to new users on signup.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', mb: 0.8, fontSize: '0.875rem' }}
            >
              Daily Login Reward
            </Typography>
            <TextField
              fullWidth
              value={dailyLoginReward}
              onChange={(e) => setDailyLoginReward(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
              Coins awarded per daily login.
            </Typography>
          </Box>
        </Box>

        {/* Row 2: Maximum Coin Balance & Coin Expiry Duration */}
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', mb: 0.8, fontSize: '0.875rem' }}
            >
              Maximum Coin Balance
            </Typography>
            <TextField
              fullWidth
              value={maxCoinBalance}
              onChange={(e) => setMaxCoinBalance(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
              Maximum coins a user can hold.
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827', mb: 0.8, fontSize: '0.875rem' }}
            >
              Coin Expiry Duration (days)
            </Typography>
            <TextField
              fullWidth
              value={coinExpiryDuration}
              onChange={(e) => setCoinExpiryDuration(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
              }}
            />
            <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
              Set 0 for no expiry.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Divider */}
      <Box sx={{ borderTop: '1px solid #F3F4F6' }} />

      {/* Feature Toggles */}
      <Box sx={{ px: 3, py: 3 }}>
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.08em',
            color: '#6B7280',
            mb: 2.5,
            display: 'block',
          }}
        >
          FEATURE TOGGLES
        </Typography>

        {/* Enable Coin Purchases */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}
            >
              Enable Coin Purchases
            </Typography>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Allow users to purchase coin packages.
            </Typography>
          </Box>
          <Switch
            checked={enableCoinPurchases}
            onChange={() => setEnableCoinPurchases(!enableCoinPurchases)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: ACCENT,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: ACCENT,
              },
            }}
          />
        </Box>

        {/* Enable Reward Tasks */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2.5,
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}
            >
              Enable Reward Tasks
            </Typography>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Allow users to earn coins via tasks.
            </Typography>
          </Box>
          <Switch
            checked={enableRewardTasks}
            onChange={() => setEnableRewardTasks(!enableRewardTasks)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: ACCENT,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: ACCENT,
              },
            }}
          />
        </Box>

        {/* Enable Consumable Purchases */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem' }}
            >
              Enable Consumable Purchases
            </Typography>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Allow spending coins on in-app items.
            </Typography>
          </Box>
          <Switch
            checked={enableConsumablePurchases}
            onChange={() => setEnableConsumablePurchases(!enableConsumablePurchases)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: ACCENT,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: ACCENT,
              },
            }}
          />
        </Box>
      </Box>

      {/* Save Button */}
      <Box sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          variant="contained"
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1.2,
            fontSize: '0.9rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: `0 4px 14px rgba(109, 83, 244, 0.35)`,
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: `0 6px 20px rgba(109, 83, 244, 0.45)`,
            },
          }}
        >
          Save Settings
        </Button>
      </Box>
    </Paper>
  );

  // ─── Render Coin Packages Tab ───
  const renderCoinPackagesTab = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Coin Packages Section */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
              Coin Packages
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
              Manage purchasable LBK coin bundles.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddPackageModal(true)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.85rem',
              bgcolor: ACCENT,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: ACCENT_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Add Package
          </Button>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Package Name</TableCell>
                <TableCell sx={thStyle}>Coins</TableCell>
                <TableCell sx={thStyle}>Price</TableCell>
                <TableCell sx={thStyle}>Bonus Coins</TableCell>
                <TableCell sx={thStyle}>Popularity</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell sx={thStyle}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coinPackages.map((pkg) => (
                <TableRow key={pkg.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#F9FAFB' } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                      {pkg.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#374151', fontSize: '0.9rem' }}>
                      {pkg.coins}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#374151', fontSize: '0.9rem' }}>
                      {pkg.price}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
                      {pkg.bonusCoins}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {pkg.popularity ? (
                      <Chip
                        label={pkg.popularity}
                        size="small"
                        sx={{
                          bgcolor: pkg.popularity === 'Popular' ? '#EBF5FF' : pkg.popularity === 'Best Value' ? '#ECFDF5' : pkg.popularity === 'Premium' ? '#F3E8FF' : '#F3F4F6',
                          color: pkg.popularity === 'Popular' ? '#3B82F6' : pkg.popularity === 'Best Value' ? '#10B981' : pkg.popularity === 'Premium' ? '#A855F7' : '#6B7280',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 24,
                          borderRadius: '6px',
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: '#9CA3AF' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pkg.status}
                      size="small"
                      sx={{
                        bgcolor: pkg.status === 'Active' ? '#EAFDF4' : '#F3F4F6',
                        color: pkg.status === 'Active' ? '#10B981' : '#6B7280',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '6px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditPackage(pkg)}
                        sx={{ color: '#9CA3AF', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ color: '#9CA3AF', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}
                      >
                        <MoreIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Consumable Purchases Section */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}>
              Consumable Purchases
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
              Manage items users can buy using LBK Coins.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddConsumableModal(true)}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 0.8,
              fontSize: '0.85rem',
              bgcolor: ACCENT,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: ACCENT_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Add Configuration
          </Button>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Item</TableCell>
                <TableCell sx={thStyle}>Coins Required</TableCell>
                <TableCell sx={thStyle}>Description</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell sx={thStyle} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {consumables.map((cons) => (
                <TableRow key={cons.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#F9FAFB' } }}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                      {cons.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${cons.coinsRequired} Coins`}
                      size="small"
                      sx={{
                        bgcolor: '#F3E8FF',
                        color: '#9333EA',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '6px',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.9rem' }}>
                      {cons.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cons.status}
                      size="small"
                      sx={{
                        bgcolor: cons.status === 'Active' ? '#EAFDF4' : '#F3F4F6',
                        color: cons.status === 'Active' ? '#10B981' : '#6B7280',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        borderRadius: '6px',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditConsumable(cons)}
                        sx={{ color: '#9CA3AF', '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT } }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteConsumable(cons.id)}
                        sx={{ color: '#9CA3AF', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleToggleConsumableStatus(cons.id)}
                        sx={{
                          ml: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: '6px',
                          color: cons.status === 'Active' ? '#6B7280' : '#10B981',
                          borderColor: cons.status === 'Active' ? '#E5E7EB' : '#10B981',
                          '&:hover': {
                            borderColor: cons.status === 'Active' ? '#D1D5DB' : '#059669',
                            bgcolor: cons.status === 'Active' ? '#F9FAFB' : '#F0FDF4',
                          }
                        }}
                      >
                        {cons.status === 'Active' ? 'Disable' : 'Enable'}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  // ─── Render Earning Tasks Tab ───
  const renderEarningTasksTab = () => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #F3F4F6',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
          >
            Coin Earning Tasks
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
            Manage reward actions available to users.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            py: 1,
            fontSize: '0.82rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: '0 2px 8px rgba(109, 83, 244, 0.3)',
            },
          }}
        >
          Add Task
        </Button>
      </Box>

      {/* Tasks Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={thStyle}>Task Name</TableCell>
              <TableCell sx={thStyle}>Reward Coins</TableCell>
              <TableCell sx={thStyle}>Requirement</TableCell>
              <TableCell sx={thStyle}>Status</TableCell>
              <TableCell sx={thStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mockEarningTasks.map((task) => (
              <TableRow
                key={task.id}
                sx={{
                  '&:hover': { bgcolor: '#FAFAFE' },
                  '& td': {
                    borderBottom: '1px solid #F3F4F6',
                    py: 2,
                    fontSize: '0.875rem',
                  },
                }}
              >
                <TableCell>
                  <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                    {task.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`+${task.rewardCoins} coins`}
                    size="small"
                    sx={{
                      bgcolor: '#F0FDF4',
                      color: '#16A34A',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 26,
                      borderRadius: '6px',
                      border: 'none',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                    {task.requirement}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      color: '#16A34A',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                    }}
                  >
                    {task.status}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      sx={{
                        color: '#9CA3AF',
                        '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{
                        color: '#9CA3AF',
                        '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                      }}
                    >
                      <MoreIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  // ─── Render Rewards Tab ───
  const renderRewardsTab = () => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #F3F4F6',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827' }}
          >
            Reward System
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
            Manage achievements and milestone rewards.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={() => setOpenAddRewardModal(true)}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            py: 1,
            fontSize: '0.82rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: '0 2px 8px rgba(109, 83, 244, 0.3)',
            },
          }}
        >
          Add Reward
        </Button>
      </Box>

      {/* Rewards Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={thStyle}>Reward Name</TableCell>
              <TableCell sx={thStyle}>Reward Type</TableCell>
              <TableCell sx={thStyle}>Reward Value</TableCell>
              <TableCell sx={thStyle}>Users Earned</TableCell>
              <TableCell sx={thStyle}>Status</TableCell>
              <TableCell sx={thStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rewards.map((reward) => (
              <TableRow
                key={reward.id}
                sx={{
                  '&:hover': { bgcolor: '#FAFAFE' },
                  '& td': {
                    borderBottom: '1px solid #F3F4F6',
                    py: 2,
                    fontSize: '0.875rem',
                  },
                }}
              >
                <TableCell>
                  <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                    {reward.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={reward.type}
                    size="small"
                    sx={{
                      bgcolor: '#F0EDFE',
                      color: ACCENT,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 26,
                      borderRadius: '6px',
                      border: 'none',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
                    {reward.value}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 600, color: '#111827' }}>
                    {reward.usersEarned}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={reward.status}
                    size="small"
                    sx={{
                      bgcolor: '#EAFDF4',
                      color: '#10B981',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 26,
                      borderRadius: '6px',
                      border: 'none',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditModal(reward)}
                      sx={{
                        color: '#9CA3AF',
                        '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                      }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleOpenRewardMenu(e, reward)}
                      sx={{
                        color: '#9CA3AF',
                        '&:hover': { color: ACCENT, bgcolor: ACCENT_LIGHT },
                      }}
                    >
                      <MoreIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Reward Context Menu */}
      <Menu
        anchorEl={rewardMenuAnchor}
        open={Boolean(rewardMenuAnchor)}
        onClose={handleCloseRewardMenu}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #F3F4F6',
            minWidth: 170,
            py: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={handleDuplicateReward}
          sx={{
            py: 1.2,
            px: 2,
            fontSize: '0.875rem',
            color: '#374151',
            '&:hover': { bgcolor: '#F9FAFB' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DuplicateIcon sx={{ fontSize: 18, color: '#6B7280' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
            Duplicate
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDeactivateReward}
          sx={{
            py: 1.2,
            px: 2,
            fontSize: '0.875rem',
            color: '#374151',
            '&:hover': { bgcolor: '#F9FAFB' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DeactivateIcon sx={{ fontSize: 18, color: '#6B7280' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}>
            {menuReward?.status === 'Active' ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteReward}
          sx={{
            py: 1.2,
            px: 2,
            fontSize: '0.875rem',
            color: '#EF4444',
            '&:hover': { bgcolor: '#FEF2F2' },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DeleteIcon sx={{ fontSize: 18, color: '#EF4444' }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: '#EF4444' }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );

  // ─── Render placeholder for other tabs ───
  const renderPlaceholderTab = (tabName) => (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        p: 6,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: ACCENT_LIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: '2rem' }}>
          {tabName === 'Coin Packages'
            ? '🪙'
            : tabName === 'Earning Tasks'
            ? '📋'
            : tabName === 'Rewards'
            ? '🎁'
            : '📊'}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
        {tabName}
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B7280', maxWidth: 400, mx: 'auto' }}>
        {tabName === 'Coin Packages' &&
          'Create and manage coin packages available for purchase by users.'}
        {tabName === 'Earning Tasks' &&
          'Set up tasks that users can complete to earn coins and rewards.'}
        {tabName === 'Rewards' &&
          'Configure the reward catalog where users can spend their earned coins.'}
        {tabName === 'Wallet Analytics' &&
          'View detailed analytics on coin circulation, revenue, and user engagement.'}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: '#9CA3AF', mt: 2, fontStyle: 'italic', fontSize: '0.78rem' }}
      >
        Coming soon
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* ─── Country / Currency Row ─── */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#374151', mb: 0.5, fontSize: '0.82rem' }}
          >
            Country
          </Typography>
          <FormControl size="small">
            <Select
              value={country}
              onChange={handleCountryChange}
              sx={{
                minWidth: 160,
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
              }}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#374151', mb: 0.5, fontSize: '0.82rem' }}
          >
            Currency
          </Typography>
          <FormControl size="small">
            <Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              sx={{
                minWidth: 160,
                borderRadius: '8px',
                bgcolor: '#F9FAFB',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
              }}
            >
              {Object.values(CURRENCY_MAP).map((cur) => (
                <MenuItem key={cur} value={cur}>
                  {cur}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
            Currency auto-fills based on country selection.
          </Typography>
        </Box>
      </Box>

      {/* ─── Page Header ─── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: '#111827', fontSize: '1.5rem' }}
          >
            LBK Wallet Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.3 }}>
            Manage coin packages, rewards, earning tasks and wallet settings.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.2,
            fontSize: '0.9rem',
            bgcolor: ACCENT,
            color: '#fff',
            boxShadow: `0 4px 14px rgba(109, 83, 244, 0.35)`,
            '&:hover': {
              bgcolor: ACCENT_HOVER,
              boxShadow: `0 6px 20px rgba(109, 83, 244, 0.45)`,
            },
          }}
        >
          Create Coin Package
        </Button>
      </Box>

      {/* ─── Stats Cards ─── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {STATS.map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            sx={{
              flex: 1,
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              p: 2.5,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.78rem',
                  mb: 0.5,
                  whiteSpace: 'pre-line',
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1.75rem',
                  color: '#111827',
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {stat.value}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {stat.positive ? (
                  <TrendUpIcon sx={{ fontSize: 14, color: '#16A34A' }} />
                ) : (
                  <TrendDownIcon sx={{ fontSize: 14, color: '#DC2626' }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: stat.positive ? '#16A34A' : '#DC2626',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                  }}
                >
                  {stat.change}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}
                >
                  vs last month
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: stat.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: stat.iconColor,
                flexShrink: 0,
              }}
            >
              {stat.icon}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ─── Sub-Tabs ─── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #E5E7EB',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0 }}>
          {SUB_TABS.map((tab) => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              sx={{
                textTransform: 'none',
                fontWeight: activeTab === tab ? 700 : 500,
                fontSize: '0.9rem',
                px: 2.5,
                py: 1.5,
                borderRadius: 0,
                color: activeTab === tab ? ACCENT : '#6B7280',
                borderBottom:
                  activeTab === tab
                    ? `2.5px solid ${ACCENT}`
                    : '2.5px solid transparent',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: ACCENT,
                },
                transition: 'all 0.2s ease',
              }}
            >
              {tab}
            </Button>
          ))}
        </Box>
      </Box>

      {/* ─── Tab Content ─── */}
      {activeTab === 'Coin Packages' && renderCoinPackagesTab()}
      {activeTab === 'Settings' && renderSettingsTab()}
      {activeTab === 'Earning Tasks' && renderEarningTasksTab()}
      {activeTab === 'Rewards' && renderRewardsTab()}
      {!['Coin Packages', 'Settings', 'Earning Tasks', 'Rewards'].includes(activeTab) && renderPlaceholderTab(activeTab)}

      {/* ─── Add Reward Dialog ─── */}
      <Dialog
        open={openAddRewardModal}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
              Add Reward
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>
              Create a new milestone reward.
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleCloseModal}
            sx={{ color: '#9CA3AF', '&:hover': { color: '#4B5563' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          {/* Reward Name */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Reward Name <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Profile Verification"
              value={newRewardName}
              onChange={(e) => setNewRewardName(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.875rem',
                }
              }}
            />
          </Box>

          {/* Row 2: Reward Type & Reward Value */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Type
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Free Connect"
                value={newRewardType}
                onChange={(e) => setNewRewardType(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Value <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 5 Uses"
                value={newRewardValue}
                onChange={(e) => setNewRewardValue(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
          </Box>

          {/* Status */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Active Button */}
              <Box
                onClick={() => setNewRewardStatus('Active')}
                sx={{
                  flex: 1,
                  py: 1.2,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  border: newRewardStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB',
                  bgcolor: newRewardStatus === 'Active' ? '#EAFDF4' : '#F9FAFB',
                  color: newRewardStatus === 'Active' ? '#10B981' : '#9CA3AF',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: newRewardStatus === 'Active' ? '1px solid #10B981' : '1px solid #D1D5DB',
                  }
                }}
              >
                Active
              </Box>

              {/* Inactive Button */}
              <Box
                onClick={() => setNewRewardStatus('Inactive')}
                sx={{
                  flex: 1,
                  py: 1.2,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  border: newRewardStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB',
                  bgcolor: newRewardStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB',
                  color: newRewardStatus === 'Inactive' ? '#4B5563' : '#9CA3AF',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: newRewardStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #D1D5DB',
                  }
                }}
              >
                Inactive
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              color: '#374151',
              border: '1px solid #E5E7EB',
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: '#F9FAFB',
                borderColor: '#D1D5DB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRewardSubmit}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              bgcolor: ACCENT,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: ACCENT_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Add Reward
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Edit Reward Dialog ─── */}
      <Dialog
        open={openEditRewardModal}
        onClose={handleCloseEditModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
              Edit Reward
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>
              Edit an existing milestone reward.
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleCloseEditModal}
            sx={{ color: '#9CA3AF', '&:hover': { color: '#4B5563' } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          {/* Reward Name */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Reward Name <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. Profile Verification"
              value={editRewardName}
              onChange={(e) => setEditRewardName(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: ACCENT },
                  '&.Mui-focused fieldset': { borderColor: ACCENT },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '0.875rem',
                }
              }}
            />
          </Box>

          {/* Row 2: Reward Type & Reward Value */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Type
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. Free Connect"
                value={editRewardType}
                onChange={(e) => setEditRewardType(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
                Reward Value <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 5 Uses"
                value={editRewardValue}
                onChange={(e) => setEditRewardValue(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                  },
                  '& .MuiOutlinedInput-input': {
                    fontSize: '0.875rem',
                  }
                }}
              />
            </Box>
          </Box>

          {/* Status */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>
              Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Active Button */}
              <Box
                onClick={() => setEditRewardStatus('Active')}
                sx={{
                  flex: 1,
                  py: 1.2,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  border: editRewardStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB',
                  bgcolor: editRewardStatus === 'Active' ? '#EAFDF4' : '#F9FAFB',
                  color: editRewardStatus === 'Active' ? '#10B981' : '#9CA3AF',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: editRewardStatus === 'Active' ? '1px solid #10B981' : '1px solid #D1D5DB',
                  }
                }}
              >
                Active
              </Box>

              {/* Inactive Button */}
              <Box
                onClick={() => setEditRewardStatus('Inactive')}
                sx={{
                  flex: 1,
                  py: 1.2,
                  textAlign: 'center',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  border: editRewardStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB',
                  bgcolor: editRewardStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB',
                  color: editRewardStatus === 'Inactive' ? '#4B5563' : '#9CA3AF',
                  transition: 'all 0.2s',
                  '&:hover': {
                    border: editRewardStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #D1D5DB',
                  }
                }}
              >
                Inactive
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button
            onClick={handleCloseEditModal}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              color: '#374151',
              border: '1px solid #E5E7EB',
              bgcolor: '#fff',
              '&:hover': {
                bgcolor: '#F9FAFB',
                borderColor: '#D1D5DB',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditRewardSubmit}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              bgcolor: ACCENT,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: ACCENT_HOVER,
                boxShadow: 'none',
              },
            }}
          >
            Edit Reward
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add Coin Package Dialog ─── */}
      <Dialog open={openAddPackageModal} onClose={handleClosePackageModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>Create Coin Package</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>Add a new purchasable LBK coin bundle.</Typography>
          </Box>
          <IconButton size="small" onClick={handleClosePackageModal} sx={{ color: '#9CA3AF' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Package Name <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Starter Pack" value={pkgName} onChange={(e) => setPkgName(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Coins <span style={{ color: '#EF4444' }}>*</span></Typography>
              <TextField fullWidth placeholder="e.g. 1,200" value={pkgCoins} onChange={(e) => setPkgCoins(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Price <span style={{ color: '#EF4444' }}>*</span></Typography>
              <TextField fullWidth placeholder="e.g. ₹199" value={pkgPrice} onChange={(e) => setPkgPrice(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Bonus Coins</Typography>
              <TextField fullWidth placeholder="0" value={pkgBonus} onChange={(e) => setPkgBonus(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Popularity</Typography>
              <TextField fullWidth placeholder="" value={pkgPopularity} onChange={(e) => setPkgPopularity(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Status</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => setPkgStatus('Active')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: pkgStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB', bgcolor: pkgStatus === 'Active' ? '#EAFDF4' : '#F9FAFB', color: pkgStatus === 'Active' ? '#10B981' : '#9CA3AF' }}>Active</Box>
              <Box onClick={() => setPkgStatus('Inactive')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: pkgStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB', bgcolor: pkgStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB', color: pkgStatus === 'Inactive' ? '#4B5563' : '#9CA3AF' }}>Inactive</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleClosePackageModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSavePackage} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Create Package</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Edit Coin Package Dialog ─── */}
      <Dialog open={openEditPackageModal} onClose={handleClosePackageModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>Edit Coin Package</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>Update this LBK coin bundle.</Typography>
          </Box>
          <IconButton size="small" onClick={handleClosePackageModal} sx={{ color: '#9CA3AF' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Package Name <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Starter Pack" value={pkgName} onChange={(e) => setPkgName(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Coins <span style={{ color: '#EF4444' }}>*</span></Typography>
              <TextField fullWidth placeholder="e.g. 1,200" value={pkgCoins} onChange={(e) => setPkgCoins(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Price <span style={{ color: '#EF4444' }}>*</span></Typography>
              <TextField fullWidth placeholder="e.g. ₹199" value={pkgPrice} onChange={(e) => setPkgPrice(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Bonus Coins</Typography>
              <TextField fullWidth placeholder="0" value={pkgBonus} onChange={(e) => setPkgBonus(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Popularity</Typography>
              <TextField fullWidth placeholder="" value={pkgPopularity} onChange={(e) => setPkgPopularity(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Status</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => setPkgStatus('Active')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: pkgStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB', bgcolor: pkgStatus === 'Active' ? '#EAFDF4' : '#F9FAFB', color: pkgStatus === 'Active' ? '#10B981' : '#9CA3AF' }}>Active</Box>
              <Box onClick={() => setPkgStatus('Inactive')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: pkgStatus === 'Inactive' ? '1px solid #9CA3AF' : '1px solid #E5E7EB', bgcolor: pkgStatus === 'Inactive' ? '#F3F4F6' : '#F9FAFB', color: pkgStatus === 'Inactive' ? '#4B5563' : '#9CA3AF' }}>Inactive</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleClosePackageModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSavePackage} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add Consumable Dialog ─── */}
      <Dialog open={openAddConsumableModal} onClose={handleCloseConsumableModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>Add Configuration</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>Add a new coin-purchasable in-app item.</Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseConsumableModal} sx={{ color: '#9CA3AF' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Item Name <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Super Like" value={consName} onChange={(e) => setConsName(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Coins Required <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. 15" value={consCoins} onChange={(e) => setConsCoins(e.target.value)} size="small" InputProps={{ endAdornment: <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>coins</Typography> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Description <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Boost profile reach" value={consDesc} onChange={(e) => setConsDesc(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Status</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box onClick={() => setConsStatus('Active')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: consStatus === 'Active' ? '1px solid #10B981' : '1px solid #E5E7EB', bgcolor: consStatus === 'Active' ? '#EAFDF4' : '#F9FAFB', color: consStatus === 'Active' ? '#10B981' : '#9CA3AF' }}>Active</Box>
              <Box onClick={() => setConsStatus('Disabled')} sx={{ flex: 1, py: 1.2, textAlign: 'center', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', border: consStatus === 'Disabled' ? '1px solid #9CA3AF' : '1px solid #E5E7EB', bgcolor: consStatus === 'Disabled' ? '#F3F4F6' : '#F9FAFB', color: consStatus === 'Disabled' ? '#4B5563' : '#9CA3AF' }}>Disabled</Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleCloseConsumableModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSaveConsumable} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Add Configuration</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Edit Consumable Dialog ─── */}
      <Dialog open={openEditConsumableModal} onClose={handleCloseConsumableModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>Edit Consumable</Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 0.2 }}>Update this purchasable item.</Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseConsumableModal} sx={{ color: '#9CA3AF' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, pb: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Item Name <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Super Like" value={consName} onChange={(e) => setConsName(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Coins Required <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. 15" value={consCoins} onChange={(e) => setConsCoins(e.target.value)} size="small" InputProps={{ endAdornment: <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>coins</Typography> }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 0.8, fontSize: '0.82rem' }}>Description <span style={{ color: '#EF4444' }}>*</span></Typography>
            <TextField fullWidth placeholder="e.g. Boost profile reach" value={consDesc} onChange={(e) => setConsDesc(e.target.value)} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F9FAFB' } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={handleCloseConsumableModal} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', color: '#374151', border: '1px solid #E5E7EB' }}>Cancel</Button>
          <Button onClick={handleSaveConsumable} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, px: 3, py: 1, fontSize: '0.875rem', bgcolor: ACCENT, color: '#fff' }}>Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LBKWalletManagement;
